import { useState, useEffect } from 'react';
import { Plus, Eye, Edit, Trash2, Share, BarChart3, Users, Calendar, UserCheck, CheckCircle, Home, FileText, MessageSquare, Upload, X, Loader2, Archive, ChevronLeft, ChevronRight, Clock, TrendingUp, DollarSign, XCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
// Mock data removed - using real APIs where available
// TODO: Implement client stats API, appointments API when available
import { Property } from '../types/property';
import { PropertyCard } from './PropertyCard';
import { PropertyDetail } from './PropertyDetail';
import { ClientManagement } from './ClientManagement';
import { Appointments } from './Appointments';
import { DealPipeline } from './DealPipeline';
import { RentalManagement } from './RentalManagement';
import { TransferSignings } from './TransferSignings';
import { StockSharingFeed } from './StockSharingFeed';
import { toast } from 'sonner';
import { propertyAPI } from '../lib/property-api';
import { imageUploadAPI } from '../lib/image-upload-api';
import { getFrontendDomain, getContactEmail } from '@/config/runtime-config';
import { logger } from '@/services/logger.service';
import { getAgentViewStats, formatViewTime, AgentViewStats } from '../lib/viewing-time-api';
import { sellerPropertyAPI } from '../lib/seller-property-api';
import { SellerProperty } from '../types/seller';
import { Agent as AuthAgent } from '../lib/agent-auth-api';
import { authService } from '../lib/auth';
import { clientAPI } from '../lib/client-api';
import { appointmentAPI } from '../lib/appointment-api';
import { leadAllocationAPI } from '../lib/advanced-features-api';
import { dealAPI } from '../lib/deal-api';
import { AgentProfileEditor } from './AgentProfileEditor';
import { MyListings } from './MyListings';
import { AgentLeads } from './AgentLeads';
import { KnowledgeHub } from './KnowledgeHub';
import { AgentReferrals } from './AgentReferrals';
import apiClient from '@/services/api-client';

interface AgentHubProps {
  onBackToCatalog?: () => void;
}

export function AgentHub({}: AgentHubProps) {
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [viewMode, setViewMode] = useState<'dashboard' | 'listings' | 'leads' | 'detail' | 'clients' | 'appointments' | 'seller-properties' | 'deals' | 'rentals' | 'stock-sharing' | 'my-profile' | 'knowledge-hub' | 'referrals'>('dashboard');
  const [statusFilter, setStatusFilter] = useState<'all' | 'Published' | 'In-Review' | 'Draft'>('all');
  
  // Agent authentication state
  const [currentAgent, setCurrentAgent] = useState<AuthAgent | null>(null);

  // Backend integration state
  const [properties, setProperties] = useState<Property[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingProperty, setEditingProperty] = useState<Property | null>(null);
  
  // Check agent authentication via Cognito
  useEffect(() => {
    const user = authService.getCurrentUser();
    if (user && user.role === 'agent') {
      setCurrentAgent({
        id: user.id,
        username: user.username,
        name: user.name,
        email: user.email,
        phone: '',
        status: 'active',
        createdAt: new Date().toISOString(),
      });
      console.log('✅ Agent authenticated via Cognito:', user.name);
    } else {
      console.log('ℹ️ No agent session (using current user authentication)');
    }
  }, []);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    type: 'House' as Property['type'],
    price: '',
    street: '',
    suburb: '',
    city: '',
    province: 'Gauteng',
    postalCode: '',
    bedrooms: '',
    bathrooms: '',
    parking: '',
    erfSize: '',
    floorSize: '',
    description: '',
    amenities: [] as string[],
    publishImmediately: false,
  });
  
  // Image upload state
  const [uploadedImages, setUploadedImages] = useState<Array<{ url: string; path: string; file?: File }>>([]);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ current: number; total: number } | null>(null);

  // Server status
  const [isServerOnline, setIsServerOnline] = useState<boolean | null>(null);

  // Seller properties state
  const [sellerProperties, setSellerProperties] = useState<SellerProperty[]>([]);
  const [isLoadingSellerProperties, setIsLoadingSellerProperties] = useState(true);

  // Viewing time statistics
  const [viewStats, setViewStats] = useState<AgentViewStats | null>(null);
  const [isLoadingStats, setIsLoadingStats] = useState(true);

  // Dashboard stats (computed from real data)
  const [dashboardStats, setDashboardStats] = useState({
    totalClients: 0,
    activeClients: 0,
    qualifiedLeads: 0,
    preApprovedClients: 0,
    conversionRate: 0,
    leadsThisWeek: 0,
    totalAppointments: 0,
    upcomingAppointments: 0,
    thisWeekAppointments: 0,
    completedAppointments: 0,
  });

  const [dealStats, setDealStats] = useState({
    active: 0,
    closed: 0,
    lost: 0,
    pipelineValue: 0,
  });

  // Load properties from backend on mount
  useEffect(() => {
    checkServerHealth();
    loadProperties();
    loadViewStats();
    loadSellerProperties();
    loadDashboardStats();
  }, []);

  const loadViewStats = async () => {
    try {
      setIsLoadingStats(true);
      const stats = await getAgentViewStats();
      setViewStats(stats);
    } catch (error) {
      logger.error('Failed to load view stats', error as Error, undefined, 'AgentHub');
      // Set default empty stats on error
      setViewStats({
        totalViews: 0,
        totalViewTime: 0,
        averageViewTime: 0,
        propertyCount: 0,
        propertyStats: []
      });
    } finally {
      setIsLoadingStats(false);
    }
  };

  const loadSellerProperties = async () => {
    try {
      setIsLoadingSellerProperties(true);
      const fetchedProperties = await sellerPropertyAPI.getSellerProperties();
      
      // If no properties exist, seed with sample data
      if (fetchedProperties.length === 0) {
        console.log('No seller properties found, seeding with sample data...');
        await sellerPropertyAPI.seedSellerProperties(mockSellerProperties);
        const seededProperties = await sellerPropertyAPI.getSellerProperties();
        setSellerProperties(seededProperties);
      } else {
        setSellerProperties(fetchedProperties);
      }
    } catch (error) {
      logger.error('Failed to load seller properties:', error as Error, undefined, 'AgentHub');
      toast.error('Failed to load seller properties');
      setSellerProperties([]);
    } finally {
      setIsLoadingSellerProperties(false);
    }
  };

  const loadDashboardStats = async () => {
    try {
      const [clients, leads, appointments, deals] = await Promise.allSettled([
        clientAPI.getClients(),
        leadAllocationAPI.getAgentLeads(),
        appointmentAPI.getAppointments(),
        dealAPI.getDeals(),
      ]);

      const clientList = clients.status === 'fulfilled' ? clients.value : [];
      const leadList = leads.status === 'fulfilled' ? leads.value : [];
      const appointmentList = appointments.status === 'fulfilled' ? appointments.value : [];
      const dealList = deals.status === 'fulfilled' ? deals.value : [];

      const now = Date.now();
      const weekMs = 7 * 24 * 60 * 60 * 1000;
      const weekAgo = now - weekMs;
      const weekStart = new Date();
      weekStart.setHours(0, 0, 0, 0);
      weekStart.setDate(weekStart.getDate() - weekStart.getDay());

      const totalClients = clientList.length;
      const activeClients = clientList.filter((c: any) => c.status === 'active').length;
      const qualifiedLeads = leadList.filter((l: any) => l.status === 'qualified').length;
      const preApprovedClients = clientList.filter((c: any) => c.preApproved || c.status === 'pre-approved').length;
      const conversionRate = (leadList.length + totalClients) > 0
        ? Math.round((totalClients / (leadList.length + totalClients)) * 100)
        : 0;
      const leadsThisWeek = leadList.filter((l: any) => new Date(l.timestamp).getTime() > weekAgo).length;

      const totalAppointments = appointmentList.length;
      const upcomingAppointments = appointmentList.filter(
        (a: any) => a.status === 'Scheduled' || a.status === 'Confirmed'
      ).length;
      const thisWeekAppointments = appointmentList.filter((a: any) => {
        const d = new Date(a.date);
        return d >= weekStart && d < new Date(weekStart.getTime() + weekMs);
      }).length;
      const completedAppointments = appointmentList.filter((a: any) => a.status === 'Completed').length;

      setDashboardStats({
        totalClients,
        activeClients,
        qualifiedLeads,
        preApprovedClients,
        conversionRate,
        leadsThisWeek,
        totalAppointments,
        upcomingAppointments,
        thisWeekAppointments,
        completedAppointments,
      });

      const activeDeals = dealList.filter((d: any) => d.stage !== 'closed' && d.stage !== 'lost');
      const closedDeals = dealList.filter((d: any) => d.stage === 'closed');
      const lostDeals = dealList.filter((d: any) => d.stage === 'lost');
      const pipelineValue = activeDeals.reduce((sum: number, d: any) => sum + (d.finalAmount || d.offerAmount || 0), 0);
      setDealStats({ active: activeDeals.length, closed: closedDeals.length, lost: lostDeals.length, pipelineValue });
    } catch (error) {
      logger.error('Failed to load dashboard stats', error as Error, undefined, 'AgentHub');
    }
  };

  const checkServerHealth = async () => {
    const isOnline = await propertyAPI.checkHealth();
    setIsServerOnline(isOnline);
    if (!isOnline) {
      console.warn('⚠️ Server is offline - app will use mock data fallback');
    }
  };

  const loadProperties = async () => {
    try {
      setIsLoading(true);
      const raw = await apiClient.get<any>('/my-listings?pageSize=100');
      const data = raw?.data ?? raw;
      const fetchedProperties = (data.listings || []).map((l: any) => ({
        ...l,
        id: l.somethingPropertyId || l.id || String(l.listingId || ''),
        slug: l.somethingPropertyId || l.id || '',
        title: l.title || 'Untitled',
        status: l.status || 'draft',
      }));
      setProperties(fetchedProperties.filter((p: any) => p.status !== 'archived'));

      // Update server status based on whether we got data
      if (fetchedProperties.length > 0 && isServerOnline === null) {
        setIsServerOnline(true);
      }
    } catch (error) {
      logger.error('Error loading properties:', error as Error, undefined, 'AgentHub');
      setIsServerOnline(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle image upload (supports multiple files)
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    // Convert FileList to Array
    const filesArray = Array.from(files);
    
    // Validate all files before uploading
    const invalidFiles = filesArray.filter(file => {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} is too large (max 10MB)`);
        return true;
      }
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image file`);
        return true;
      }
      return false;
    });

    if (invalidFiles.length === filesArray.length) {
      return; // All files are invalid
    }

    const validFiles = filesArray.filter(file => !invalidFiles.includes(file));
    
    setIsUploadingImage(true);
    setUploadProgress({ current: 0, total: validFiles.length });
    
    try {
      const propertyId = editingProperty?.id;
      const uploadedResults: Array<{ url: string; path: string; file: File }> = [];
      
      // Upload files sequentially to show progress
      for (let i = 0; i < validFiles.length; i++) {
        const file = validFiles[i];
        setUploadProgress({ current: i + 1, total: validFiles.length });
        
        try {
          const { url, path } = await imageUploadAPI.uploadImage(file, propertyId);
          uploadedResults.push({ url, path, file });
        } catch (error) {
          logger.error(`Error uploading ${file.name}:`, error as Error, undefined, 'AgentHub');
          toast.error(`Failed to upload ${file.name}`);
        }
      }
      
      if (uploadedResults.length > 0) {
        setUploadedImages(prev => [...prev, ...uploadedResults]);
        toast.success(`${uploadedResults.length} image${uploadedResults.length > 1 ? 's' : ''} uploaded successfully`);
      }
    } catch (error) {
      logger.error('Error uploading images:', error as Error, undefined, 'AgentHub');
      toast.error('Failed to upload images');
    } finally {
      setIsUploadingImage(false);
      setUploadProgress(null);
      // Reset input so the same files can be selected again
      e.target.value = '';
    }
  };

  // Remove image
  const handleRemoveImage = async (index: number) => {
    const image = uploadedImages[index];
    try {
      await imageUploadAPI.deleteImage(image.path);
      setUploadedImages(prev => prev.filter((_, i) => i !== index));
      toast.success('Image removed');
    } catch (error) {
      logger.error('Error removing image:', error as Error, undefined, 'AgentHub');
      toast.error('Failed to remove image');
    }
  };

  // Reorder images (move left/right)
  const handleMoveImage = (index: number, direction: 'left' | 'right') => {
    const newIndex = direction === 'left' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= uploadedImages.length) return;
    
    setUploadedImages(prev => {
      const newArray = [...prev];
      [newArray[index], newArray[newIndex]] = [newArray[newIndex], newArray[index]];
      return newArray;
    });
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      title: '',
      type: 'House',
      price: '',
      street: '',
      suburb: '',
      city: '',
      province: 'Gauteng',
      postalCode: '',
      bedrooms: '',
      bathrooms: '',
      parking: '',
      erfSize: '',
      floorSize: '',
      description: '',
      amenities: [],
      publishImmediately: false,
    });
    setUploadedImages([]);
    setEditingProperty(null);
  };

  // Open create dialog
  const handleCreateListing = () => {
    resetForm();
    setIsCreating(true);
  };

  // Open edit dialog
  const handleEditListing = (property: Property) => {
    setEditingProperty(property);
    setFormData({
      title: property.title,
      type: property.type,
      price: (property.price ?? 0).toString(),
      street: property.address?.street || '',
      suburb: property.address?.suburb || '',
      city: property.address?.city || '',
      province: property.address?.province || '',
      postalCode: property.address?.postalCode || '',
      bedrooms: property.facts?.bedrooms?.toString() || '',
      bathrooms: property.facts?.bathrooms?.toString() || '',
      parking: property.facts?.parking?.toString() || '',
      erfSize: property.facts?.erfSize?.toString() || '',
      floorSize: property.facts?.floorSize?.toString() || '',
      description: property.description,
      amenities: property.amenities,
      publishImmediately: property.status === 'Published',
    });
    setUploadedImages((property.media ?? []).map(m => ({ url: m.url, path: m.id })));
    setIsCreating(true);
  };

  // Submit form (create or update)
  const handleSubmitListing = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.title || !formData.price || !formData.suburb || !formData.city) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (uploadedImages.length === 0) {
      toast.error('Please upload at least one image');
      return;
    }

    setIsSubmitting(true);
    try {
      const propertyData: Omit<Property, 'id'> = {
        slug: formData.title.toLowerCase().replace(/\s+/g, '-'),
        title: formData.title,
        type: formData.type,
        status: formData.publishImmediately ? 'Published' : 'Draft',
        address: {
          street: formData.street,
          suburb: formData.suburb,
          city: formData.city,
          province: formData.province,
          postalCode: formData.postalCode,
        },
        geo: {
          lat: -26.2041,
          lng: 28.0473,
        },
        price: parseFloat(formData.price),
        facts: {
          bedrooms: formData.bedrooms ? parseInt(formData.bedrooms) : undefined,
          bathrooms: formData.bathrooms ? parseInt(formData.bathrooms) : undefined,
          parking: formData.parking ? parseInt(formData.parking) : undefined,
          erfSize: formData.erfSize ? parseInt(formData.erfSize) : undefined,
          floorSize: formData.floorSize ? parseInt(formData.floorSize) : undefined,
        },
        amenities: formData.amenities,
        media: uploadedImages.map((img, index) => ({
          id: img.path,
          url: img.url,
          alt: formData.title,
          isCover: index === 0,
          order: index,
        })),
        description: formData.description,
        listingDate: new Date().toISOString(),
        agentId: 'agent-1',
        agent: {
          id: 'agent-1',
          name: 'John Doe',
          email: getContactEmail(),
          phone: '+27 12 345 6789',
          whatsapp: '+27 12 345 6789',
        },
        visibility: 'Public',
        flags: {
          featured: false,
          new: true,
        },
      };

      if (editingProperty) {
        // Update existing property
        await propertyAPI.updateProperty(editingProperty.id, propertyData);
        toast.success('Property updated successfully!');
      } else {
        // Create new property
        await propertyAPI.createProperty(propertyData);
        toast.success('Property created successfully!');
      }

      // Reload properties and close dialog
      await loadProperties();
      setIsCreating(false);
      resetForm();
    } catch (error) {
      logger.error('Error saving property:', error as Error, undefined, 'AgentHub');
      toast.error('Failed to save property. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Archive property
  const handleArchiveProperty = async (property: Property) => {
    if (!confirm(`Are you sure you want to archive "${property.title}"? You can restore it later from the Admin Portal.`)) {
      return;
    }

    try {
      await propertyAPI.archiveProperty(property.id);
      toast.success('Property archived successfully');
      await loadProperties();
    } catch (error) {
      logger.error('Error archiving property:', error as Error, undefined, 'AgentHub');
      toast.error('Failed to archive property');
    }
  };

  const handlePropertyView = (property: Property) => {
    setSelectedProperty(property);
    setViewMode('detail');
  };

  const handleShare = (property: Property, method: 'whatsapp' | 'email') => {
    const frontendDomain = getFrontendDomain();
    const shareUrl = `${frontendDomain}/properties/${property.slug}?utm_source=${method}&agentId=1`;
    
    if (method === 'whatsapp') {
      const message = `Hi - here's a property I think you'll love: ${property.title}, ${property.address?.suburb} - R${(property.price ?? 0).toLocaleString()}. Details: ${shareUrl}`;
      const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
      window.open(whatsappUrl, '_blank');
    } else {
      const subject = `Property: ${property.title}`;
      const body = `I'm sharing this property from Something Property. See photos, key facts, and start your pre-approval here: ${shareUrl}`;
      const emailUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      window.open(emailUrl, '_blank');
    }
    
    toast.success(`Share link generated for ${method}`);
  };

  const handleSellerPropertyView = (sellerProperty: any) => {
    // Convert seller property to Property format for viewing
    const convertedProperty: Property = {
      id: sellerProperty.id,
      title: sellerProperty.title,
      slug: (sellerProperty.title || '').toLowerCase().replace(/\s+/g, '-'),
      type: sellerProperty.propertyType as Property['type'] || 'House',
      status: 'Published' as const,
      price: sellerProperty.price,
      address: {
        // F-013: sellerProperty.location may be undefined if stored with flat fields
        street: sellerProperty.location?.address || (sellerProperty as any).address || '',
        suburb: sellerProperty.location?.city || (sellerProperty as any).city || '',
        city: sellerProperty.location?.city || (sellerProperty as any).city || '',
        province: sellerProperty.location?.province || (sellerProperty as any).state || 'Gauteng',
        postalCode: '0000'
      },
      geo: {
        lat: 0,
        lng: 0
      },
      facts: {
        bedrooms: sellerProperty.bedrooms,
        bathrooms: sellerProperty.bathrooms,
        parking: sellerProperty.parkingSpaces || 2,
        floorSize: sellerProperty.size,
        erfSize: sellerProperty.size
      },
      amenities: sellerProperty.features || [],
      media: sellerProperty.images ? sellerProperty.images.map((url: string, index: number) => ({
        id: `img-${index}`,
        url: url,
        alt: sellerProperty.title,
        isCover: index === 0,
        order: index
      })) : [],
      description: sellerProperty.description,
      listingDate: sellerProperty.createdDate,
      agentId: 'seller-agent',
      agent: {
        id: 'seller-agent',
        name: 'Seller Agent',
        email: getContactEmail(),
        phone: '+27 12 345 6789',
        whatsapp: '+27 12 345 6789'
      },
      visibility: 'Public' as const,
      flags: {
        new: false,
        featured: false
      }
    };
    
    setSelectedProperty(convertedProperty);
    setViewMode('detail');
  };

  if (viewMode === 'detail' && selectedProperty) {
    return (
      <PropertyDetail
        property={selectedProperty}
        onBack={() => setViewMode('seller-properties')}
        isAgentView={true}
      />
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="mb-2">
            {currentAgent ? `Welcome, ${currentAgent.name}` : 'Agent Hub'}
          </h1>
          <p className="text-muted-foreground">
            {currentAgent ? `${currentAgent.email} • Agent Account` : 'Manage your property listings and track performance'}
          </p>
        </div>
      </div>

      <Tabs value={viewMode} onValueChange={(value) => setViewMode(value as typeof viewMode)} className="space-y-6">
        <TabsList>
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="listings">My Listings</TabsTrigger>
          <TabsTrigger value="leads">Leads</TabsTrigger>
          <TabsTrigger value="deals">Deal Pipeline</TabsTrigger>
          <TabsTrigger value="rentals">Rentals</TabsTrigger>
          <TabsTrigger value="seller-properties">Seller Properties</TabsTrigger>
          <TabsTrigger value="clients">Clients</TabsTrigger>
          <TabsTrigger value="appointments">Appointments & Signings</TabsTrigger>
          <TabsTrigger value="stock-sharing">Stock Sharing Feed</TabsTrigger>
          <TabsTrigger value="knowledge-hub" data-testid="agenthub-tab-knowledge-hub">Knowledge Hub</TabsTrigger>
          <TabsTrigger value="referrals" data-testid="agenthub-tab-referrals">Referrals</TabsTrigger>
          <TabsTrigger value="my-profile">My Profile</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="space-y-6">
          {/* Property Stats Cards */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Property Listings</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card data-testid="analytics-agent-total-listings-card">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium" data-testid="analytics-agent-total-listings-label">Total Listings</CardTitle>
                <BarChart3 className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold" data-testid="analytics-agent-total-listings-count">{properties.length}</div>
                <p className="text-xs text-muted-foreground" data-testid="analytics-agent-listings-breakdown">
                  {properties.filter((p: any) => p.status === 'active').length} published, {properties.filter((p: any) => p.status === 'draft').length} draft
                </p>
              </CardContent>
            </Card>

            <Card data-testid="analytics-agent-total-views-card">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium" data-testid="analytics-agent-total-views-label">Total Views</CardTitle>
                <Eye className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                {isLoadingStats ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-sm text-muted-foreground">Loading...</span>
                  </div>
                ) : (
                  <>
                    <div className="text-2xl font-bold" data-testid="analytics-agent-total-views-count">{viewStats?.totalViews || 0}</div>
                    <p className="text-xs text-muted-foreground">
                      Across all listings
                    </p>
                  </>
                )}
              </CardContent>
            </Card>

            <Card data-testid="analytics-agent-leads-week-card">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium" data-testid="analytics-agent-leads-week-label">Leads This Week</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold" data-testid="analytics-agent-leads-week-count">{dashboardStats.leadsThisWeek}</div>
                <p className="text-xs text-muted-foreground" data-testid="analytics-agent-leads-week-change">
                  Last 7 days
                </p>
              </CardContent>
            </Card>

            <Card data-testid="analytics-agent-avg-viewing-time-card">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium" data-testid="analytics-agent-avg-viewing-time-label">Avg. Viewing Time</CardTitle>
                <Clock className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                {isLoadingStats ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-sm text-muted-foreground">Loading...</span>
                  </div>
                ) : (
                  <>
                    <div className="text-2xl font-bold" data-testid="analytics-agent-avg-viewing-time-value">
                      {viewStats?.averageViewTime ? formatViewTime(viewStats.averageViewTime) : '0s'}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Per property view
                    </p>
                  </>
                )}
              </CardContent>
            </Card>
            </div>
          </div>

          {/* Client Stats Cards */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Client Management</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card data-testid="analytics-agent-total-clients-card">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium" data-testid="analytics-agent-total-clients-label">Total Clients</CardTitle>
                  <Users className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold" data-testid="analytics-agent-total-clients-count">{dashboardStats.totalClients}</div>
                  <p className="text-xs text-muted-foreground" data-testid="analytics-agent-active-clients-count">
                    {dashboardStats.activeClients} active clients
                  </p>
                </CardContent>
              </Card>

              <Card data-testid="analytics-agent-qualified-leads-card">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium" data-testid="analytics-agent-qualified-leads-label">Qualified Leads</CardTitle>
                  <UserCheck className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold" data-testid="analytics-agent-qualified-leads-count">{dashboardStats.qualifiedLeads}</div>
                  <p className="text-xs text-muted-foreground">
                    Ready for viewings
                  </p>
                </CardContent>
              </Card>

              <Card data-testid="analytics-agent-pre-approved-card">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium" data-testid="analytics-agent-pre-approved-label">Pre-Approved</CardTitle>
                  <CheckCircle className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold" data-testid="analytics-agent-pre-approved-count">{dashboardStats.preApprovedClients}</div>
                  <p className="text-xs text-muted-foreground">
                    Ready to purchase
                  </p>
                </CardContent>
              </Card>

              <Card data-testid="analytics-agent-conversion-rate-card">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium" data-testid="analytics-agent-conversion-rate-label">Conversion Rate</CardTitle>
                  <BarChart3 className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold" data-testid="analytics-agent-conversion-rate-value">{dashboardStats.conversionRate}%</div>
                  <p className="text-xs text-muted-foreground">
                    Leads to sales
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Deal Pipeline Stats */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Deal Pipeline</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card data-testid="analytics-agent-active-deals-card">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Active Deals</CardTitle>
                  <TrendingUp className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold" data-testid="analytics-agent-active-deals-count">{dealStats.active}</div>
                  <p className="text-xs text-muted-foreground">In progress</p>
                </CardContent>
              </Card>

              <Card data-testid="analytics-agent-pipeline-value-card">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Pipeline Value</CardTitle>
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold" data-testid="analytics-agent-pipeline-value">
                    R{(dealStats.pipelineValue / 1_000_000).toFixed(1)}M
                  </div>
                  <p className="text-xs text-muted-foreground">Active deal value</p>
                </CardContent>
              </Card>

              <Card data-testid="analytics-agent-closed-deals-card">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Closed Deals</CardTitle>
                  <CheckCircle className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-emerald-400" data-testid="analytics-agent-closed-deals-count">{dealStats.closed}</div>
                  <p className="text-xs text-muted-foreground">Successfully closed</p>
                </CardContent>
              </Card>

              <Card data-testid="analytics-agent-lost-deals-card">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Lost Deals</CardTitle>
                  <XCircle className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-muted-foreground" data-testid="analytics-agent-lost-deals-count">{dealStats.lost}</div>
                  <p className="text-xs text-muted-foreground">
                    <button className="underline text-primary hover:no-underline" onClick={() => setViewMode('deals')}>View full pipeline</button>
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Seller Properties */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Seller Properties</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card data-testid="analytics-agent-seller-properties-card">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium" data-testid="analytics-agent-seller-properties-label">Available Properties</CardTitle>
                  <Home className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold" data-testid="analytics-agent-seller-properties-count">{sellerProperties.filter(p => p.status === 'approved').length}</div>
                  <p className="text-xs text-muted-foreground">
                    Approved listings from sellers
                  </p>
                </CardContent>
              </Card>

              <Card className="md:col-span-3">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium">Latest Seller Listings</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {sellerProperties.filter(p => p.status === 'approved').slice(0, 3).map((property) => (
                      <div key={property.id} className="flex items-center justify-between text-sm">
                        <div>
                          <p className="font-medium">{property.title}</p>
                          <p className="text-muted-foreground text-xs">
                            {property.location?.city} • R{(property.price ?? 0).toLocaleString()}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setViewMode('seller-properties')}
                          data-testid="agent-seller-property-view"
                        >
                          View
                        </Button>
                      </div>
                    ))}
                    {sellerProperties.filter(p => p.status === 'approved').length === 0 && (
                      <p className="text-muted-foreground text-xs">No seller properties yet</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Appointment Stats Cards */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Appointments</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card data-testid="analytics-agent-total-appointments-card">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium" data-testid="analytics-agent-total-appointments-label">Total Appointments</CardTitle>
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold" data-testid="analytics-agent-total-appointments-count">{dashboardStats.totalAppointments}</div>
                  <p className="text-xs text-muted-foreground">
                    All time appointments
                  </p>
                </CardContent>
              </Card>

              <Card data-testid="analytics-agent-upcoming-appointments-card">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium" data-testid="analytics-agent-upcoming-appointments-label">Upcoming</CardTitle>
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold" data-testid="analytics-agent-upcoming-appointments-count">{dashboardStats.upcomingAppointments}</div>
                  <p className="text-xs text-muted-foreground">
                    Scheduled & confirmed
                  </p>
                </CardContent>
              </Card>

              <Card data-testid="analytics-agent-week-appointments-card">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium" data-testid="analytics-agent-week-appointments-label">This Week</CardTitle>
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold" data-testid="analytics-agent-week-appointments-count">{dashboardStats.thisWeekAppointments}</div>
                  <p className="text-xs text-muted-foreground">
                    Current week
                  </p>
                </CardContent>
              </Card>

              <Card data-testid="analytics-agent-completed-appointments-card">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium" data-testid="analytics-agent-completed-appointments-label">Completed</CardTitle>
                  <CheckCircle className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold" data-testid="analytics-agent-completed-appointments-count">{dashboardStats.completedAppointments}</div>
                  <p className="text-xs text-muted-foreground">
                    Successfully completed
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Recent Activity */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Activity</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">New inquiry for 2-Bed Apartment in Sandton</p>
                    <p className="text-xs text-muted-foreground">2 hours ago</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">Property shared via WhatsApp</p>
                    <p className="text-xs text-muted-foreground">4 hours ago</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">Listing updated: 4-Bedroom Family Home</p>
                    <p className="text-xs text-muted-foreground">1 day ago</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="listings" className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">My Listings</h3>
            <Button onClick={handleCreateListing} data-testid="agent-listing-create">
              <Plus className="w-4 h-4 mr-2" />
              New Listing
            </Button>
          </div>
          <MyListings />
        </TabsContent>

        <TabsContent value="leads" className="space-y-6">
          <AgentLeads agentId={currentAgent?.id || ''} />
        </TabsContent>

        <TabsContent value="clients" className="space-y-6">
          <ClientManagement />
        </TabsContent>

        <TabsContent value="seller-properties" className="space-y-6">
          <div>
            <h3>Seller Properties</h3>
            <p className="text-muted-foreground mb-4">
              View approved properties listed by property owners
            </p>
          </div>

          {isLoadingSellerProperties ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-16">
                <Loader2 className="h-12 w-12 animate-spin text-muted-foreground mb-4" />
                <p className="text-muted-foreground">
                  Loading seller properties...
                </p>
              </CardContent>
            </Card>
          ) : sellerProperties.filter(p => p.status === 'approved').length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-16">
                <Home className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">
                  No approved seller properties available yet
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {sellerProperties.filter(p => p.status === 'approved').map((property) => {
                const seller = null; // TODO: Fetch seller info from API
                return (
                  <Card key={property.id}>
                    <CardContent className="p-6">
                      <div className="flex gap-4">
                        {/* Property Images */}
                        {property.images && property.images.length > 0 && (
                          <div className="flex-shrink-0">
                            <img
                              src={property.images[0]}
                              alt={property.title}
                              className="w-32 h-32 object-cover rounded-lg"
                            />
                          </div>
                        )}

                        {/* Property Details */}
                        <div className="flex-1">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h4 className="font-medium mb-1">{property.title}</h4>
                              <p className="text-sm text-muted-foreground">
                                {property.location?.address || (property as any).address || '—'}, {property.location?.city || (property as any).city || ''}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="font-semibold">R {(property.price ?? 0).toLocaleString()}</p>
                              <Badge variant="outline" className="mt-1">
                                {property.listingType}
                              </Badge>
                            </div>
                          </div>

                          <div className="flex gap-4 text-sm text-muted-foreground mb-3">
                            <span>{property.bedrooms ?? '—'} Beds</span>
                            <span>•</span>
                            <span>{property.bathrooms ?? '—'} Baths</span>
                            <span>•</span>
                            <span>{property.size ?? '—'} m²</span>
                            <span>•</span>
                            <span>{property.propertyType || '—'}</span>
                          </div>

                          <p className="text-sm mb-3 line-clamp-2">{property.description || '—'}</p>

                          {(property.features?.length ?? 0) > 0 && (
                            <div className="flex flex-wrap gap-1 mb-3">
                              {property.features.slice(0, 5).map((feature, idx) => (
                                <Badge key={idx} variant="secondary" className="text-xs">
                                  {feature}
                                </Badge>
                              ))}
                              {property.features.length > 5 && (
                                <Badge variant="secondary" className="text-xs">
                                  +{property.features.length - 5} more
                                </Badge>
                              )}
                            </div>
                          )}

                          <div className="flex items-center justify-between pt-3 border-t">
                            <div className="text-xs text-muted-foreground">
                              <p>Listed by: {seller?.name || 'Unknown Seller'}</p>
                              <p>Listed: {property.createdDate ? new Date(property.createdDate).toLocaleDateString() : '—'}</p>
                            </div>

                            <div className="flex gap-2">
                              <Button size="sm" variant="outline" onClick={() => handleSellerPropertyView(property)} data-testid="agent-seller-property-view-details">
                                <Eye className="h-4 w-4 mr-2" />
                                View Details
                              </Button>
                              <Dialog>
                                <DialogTrigger asChild>
                                  <Button size="sm" data-testid="agent-seller-property-share-trigger">
                                    <Share className="h-4 w-4 mr-2" />
                                    Share
                                  </Button>
                                </DialogTrigger>
                                <DialogContent>
                                  <DialogHeader>
                                    <DialogTitle>Share Property</DialogTitle>
                                    <DialogDescription>
                                      Share this property with potential buyers
                                    </DialogDescription>
                                  </DialogHeader>
                                  <div className="space-y-4">
                                    <p className="text-sm text-muted-foreground">
                                      {property.title}
                                    </p>
                                    <div className="grid grid-cols-2 gap-3">
                                      <Button
                                        onClick={() => {
                                          const message = `Hi - check out this property: ${property.title}, ${property.location?.city} - R${(property.price ?? 0).toLocaleString()}`;
                                          const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
                                          window.open(whatsappUrl, '_blank');
                                          toast.success('WhatsApp share link opened');
                                        }}
                                        className="w-full"
                                        data-testid="agent-seller-property-share-whatsapp"
                                      >
                                        Share via WhatsApp
                                      </Button>
                                      <Button
                                        variant="outline"
                                        onClick={() => {
                                          const subject = `Property: ${property.title}`;
                                          const body = `Check out this property: ${property.title}, ${property.location?.address}, ${property.location?.city} - R${(property.price ?? 0).toLocaleString()}`;
                                          const emailUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
                                          window.open(emailUrl, '_blank');
                                          toast.success('Email client opened');
                                        }}
                                        className="w-full"
                                        data-testid="agent-seller-property-share-email"
                                      >
                                        Share via Email
                                      </Button>
                                    </div>
                                  </div>
                                </DialogContent>
                              </Dialog>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="deals">
          <DealPipeline />
        </TabsContent>

        <TabsContent value="rentals">
          <RentalManagement />
        </TabsContent>

        <TabsContent value="appointments">
          <Appointments />
        </TabsContent>

        <TabsContent value="transfer-signings">
          <TransferSignings />
        </TabsContent>

        <TabsContent value="stock-sharing">
          <StockSharingFeed
            source="internal"
            availableProperties={[]} // TODO: Fetch from property API
            onViewProperty={(propertyId) => {
              // TODO: Fetch property from API
              console.log('View property:', propertyId);
            }}
          />
        </TabsContent>

        <TabsContent value="knowledge-hub">
          <KnowledgeHub />
        </TabsContent>

        <TabsContent value="referrals">
          <AgentReferrals />
        </TabsContent>

        <TabsContent value="my-profile">
          <AgentProfileEditor />
        </TabsContent>
      </Tabs>

      {/* Create/Edit Listing Dialog */}
      <Dialog open={isCreating} onOpenChange={(open) => {
        setIsCreating(open);
        if (!open) resetForm();
      }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingProperty ? 'Edit Listing' : 'Create New Listing'}</DialogTitle>
            <DialogDescription>
              {editingProperty ? 'Update the property details' : 'Fill in the property details to create a new listing'}
            </DialogDescription>
          </DialogHeader>
          
          <form className="space-y-6" onSubmit={handleSubmitListing}>
            {/* Image Upload Section */}
            <div className="space-y-3">
              <Label>Property Images *</Label>
              <div className="border-2 border-dashed rounded-lg p-4">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageUpload}
                  className="hidden"
                  id="image-upload"
                  disabled={isUploadingImage}
                />
                <label
                  htmlFor="image-upload"
                  className="flex flex-col items-center justify-center cursor-pointer py-4"
                >
                  {isUploadingImage ? (
                    <>
                      <Loader2 className="w-8 h-8 animate-spin text-muted-foreground mb-2" />
                      {uploadProgress && (
                        <p className="text-sm text-muted-foreground">
                          Uploading {uploadProgress.current} of {uploadProgress.total}...
                        </p>
                      )}
                    </>
                  ) : (
                    <>
                      <Upload className="w-8 h-8 text-muted-foreground mb-2" />
                      <p className="text-sm text-muted-foreground">
                        Click to upload multiple images (max 10MB each)
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        First image will be the cover photo
                      </p>
                    </>
                  )}
                </label>
                
                {/* Image Preview Grid */}
                {uploadedImages.length > 0 && (
                  <div className="grid grid-cols-3 gap-3 mt-4">
                    {uploadedImages.map((image, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={image.url}
                          alt={`Upload ${index + 1}`}
                          className="w-full h-32 object-cover rounded-lg border-2 border-transparent group-hover:border-primary/50 transition-all"
                        />
                        
                        {/* Image controls */}
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-1">
                          {/* Move left */}
                          {index > 0 && (
                            <button
                              type="button"
                              onClick={() => handleMoveImage(index, 'left')}
                              className="bg-white/90 hover:bg-white text-black rounded-full p-1.5"
                              title="Move left"
                              data-testid="agent-image-move-left"
                            >
                              <ChevronLeft className="w-4 h-4" />
                            </button>
                          )}

                          {/* Remove */}
                          <button
                            type="button"
                            onClick={() => handleRemoveImage(index)}
                            className="bg-red-500 hover:bg-red-600 text-white rounded-full p-1.5"
                            title="Remove"
                            data-testid="agent-image-remove"
                          >
                            <X className="w-4 h-4" />
                          </button>

                          {/* Move right */}
                          {index < uploadedImages.length - 1 && (
                            <button
                              type="button"
                              onClick={() => handleMoveImage(index, 'right')}
                              className="bg-white/90 hover:bg-white text-black rounded-full p-1.5"
                              title="Move right"
                              data-testid="agent-image-move-right"
                            >
                              <ChevronRight className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                        
                        {/* Cover badge */}
                        {index === 0 && (
                          <Badge className="absolute top-2 left-2 text-xs bg-primary text-primary-foreground">
                            Cover Photo
                          </Badge>
                        )}
                        
                        {/* Image number */}
                        <Badge variant="secondary" className="absolute bottom-2 right-2 text-xs">
                          {index + 1}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Image count */}
                {uploadedImages.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-2">
                    {uploadedImages.length} image{uploadedImages.length !== 1 ? 's' : ''} uploaded
                  </p>
                )}
              </div>
            </div>

            {/* Basic Information */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="title">Property Title *</Label>
                <Input
                  id="title"
                  placeholder="e.g., 3-Bedroom House in Sandton"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="type">Property Type *</Label>
                <Select value={formData.type} onValueChange={(value) => setFormData({ ...formData, type: value as Property['type'] })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="House">House</SelectItem>
                    <SelectItem value="Apartment">Apartment</SelectItem>
                    <SelectItem value="Townhouse">Townhouse</SelectItem>
                    <SelectItem value="Land">Land</SelectItem>
                    <SelectItem value="Commercial">Commercial</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Price and Location */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="price">Price (ZAR) *</Label>
                <Input
                  id="price"
                  type="number"
                  placeholder="1500000"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="street">Street Address</Label>
                <Input
                  id="street"
                  placeholder="123 Main Street"
                  value={formData.street}
                  onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="suburb">Suburb *</Label>
                <Input
                  id="suburb"
                  placeholder="Sandton"
                  value={formData.suburb}
                  onChange={(e) => setFormData({ ...formData, suburb: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="city">City *</Label>
                <Input
                  id="city"
                  placeholder="Johannesburg"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="province">Province</Label>
                <Select value={formData.province} onValueChange={(value) => setFormData({ ...formData, province: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Gauteng">Gauteng</SelectItem>
                    <SelectItem value="Western Cape">Western Cape</SelectItem>
                    <SelectItem value="KwaZulu-Natal">KwaZulu-Natal</SelectItem>
                    <SelectItem value="Eastern Cape">Eastern Cape</SelectItem>
                    <SelectItem value="Free State">Free State</SelectItem>
                    <SelectItem value="Limpopo">Limpopo</SelectItem>
                    <SelectItem value="Mpumalanga">Mpumalanga</SelectItem>
                    <SelectItem value="North West">North West</SelectItem>
                    <SelectItem value="Northern Cape">Northern Cape</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Property Facts */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="bedrooms">Bedrooms</Label>
                <Input
                  id="bedrooms"
                  type="number"
                  placeholder="3"
                  value={formData.bedrooms}
                  onChange={(e) => setFormData({ ...formData, bedrooms: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="bathrooms">Bathrooms</Label>
                <Input
                  id="bathrooms"
                  type="number"
                  placeholder="2"
                  value={formData.bathrooms}
                  onChange={(e) => setFormData({ ...formData, bathrooms: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="parking">Parking Spaces</Label>
                <Input
                  id="parking"
                  type="number"
                  placeholder="2"
                  value={formData.parking}
                  onChange={(e) => setFormData({ ...formData, parking: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="erfSize">Erf Size (m²)</Label>
                <Input
                  id="erfSize"
                  type="number"
                  placeholder="500"
                  value={formData.erfSize}
                  onChange={(e) => setFormData({ ...formData, erfSize: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="floorSize">Floor Size (m²)</Label>
                <Input
                  id="floorSize"
                  type="number"
                  placeholder="250"
                  value={formData.floorSize}
                  onChange={(e) => setFormData({ ...formData, floorSize: e.target.value })}
                />
              </div>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Describe the property features, location benefits, and unique selling points..."
                rows={5}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>

            {/* Publish Toggle */}
            <div className="flex items-center space-x-2">
              <Switch
                id="publish"
                checked={formData.publishImmediately}
                onCheckedChange={(checked) => setFormData({ ...formData, publishImmediately: checked })}
              />
              <Label htmlFor="publish">Publish immediately (otherwise save as draft)</Label>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsCreating(false);
                  resetForm();
                }}
                disabled={isSubmitting}
                data-testid="agent-listing-form-cancel"
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting || isUploadingImage} data-testid="agent-listing-form-submit">
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {editingProperty ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  editingProperty ? 'Update Listing' : 'Create Listing'
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}